import { Button } from "@dropbox/dig-components/buttons";
import { Stack } from "@dropbox/dig-foundations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashProfilePage } from "@mirage/analytics/events/types/click_dash_profile_page";
import {
  UserProfileCard,
  UserProfileCardSkeleton,
} from "@mirage/mosaics/BaseProfilePage/UserProfileCard";
import {
  CollapsibleSection,
  CollapsibleSectionTitle,
} from "@mirage/mosaics/UserProfilePage/CollapsibleSection";
import type { BasicUserInfo } from "@mirage/mosaics/UserProfilePage/types";
import i18n from "@mirage/translations";
import { useEffect, useState } from "react";

import styles from "./ProfileEmployees.module.css";

interface ProfileEmployeesProps {
  pageUuid: string;
  sectionTitle: string;
  employees: BasicUserInfo[];
  peopleType:
    | "peers"
    | "reports"
    | "new_arrivals"
    | "work_anniversaries"
    | "birthdays";
  emptyContent?: React.ReactNode;
  isLoading?: boolean;
  alwaysShowAll: boolean;
  fullWidth?: boolean;
  cardType?: "birthday" | "work_anniversary";
}

const MAX_EMPLOYEES_FOR_LIMITED_DISPLAY = 9;

export const ProfileEmployees = ({
  pageUuid,
  sectionTitle,
  employees,
  peopleType,
  emptyContent,
  isLoading,
  alwaysShowAll,
  fullWidth,
  cardType,
}: ProfileEmployeesProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const { reportPapEvent } = useMirageAnalyticsContext();

  useEffect(() => {
    setIsOpen(true);
  }, [pageUuid]);

  useEffect(() => {
    if (employees.length === 0 && !isLoading && emptyContent) {
      // Default to closed if we would normally show `emptyContent`
      setIsOpen(false);
    }
  }, [employees, isLoading, emptyContent]);

  if (employees.length === 0 && emptyContent === undefined) return null;

  const truncatable =
    !alwaysShowAll && employees.length > MAX_EMPLOYEES_FOR_LIMITED_DISPLAY;
  const employeesToShow =
    truncatable && !showAll
      ? employees.slice(0, MAX_EMPLOYEES_FOR_LIMITED_DISPLAY)
      : employees;

  const cards = isLoading ? (
    <>
      <UserProfileCardSkeleton fullWidth={!!fullWidth} />
      <UserProfileCardSkeleton fullWidth={!!fullWidth} />
      <UserProfileCardSkeleton fullWidth={!!fullWidth} />
    </>
  ) : (
    employeesToShow.map((person) => {
      return (
        <UserProfileCard
          key={`user-profile-page-person__${person.account_id}`}
          user={person}
          peopleType={peopleType}
          featureLine="people"
          fullWidth={!!fullWidth}
          cardType={cardType || "default"}
        />
      );
    })
  );

  return (
    <Stack>
      <CollapsibleSectionTitle
        sectionTitle={sectionTitle}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
      <CollapsibleSection isOpen={isOpen}>
        {employees.length || isLoading ? (
          <div className={styles.profileReportsList}>{cards}</div>
        ) : (
          emptyContent
        )}
      </CollapsibleSection>
      {truncatable && isOpen ? (
        <Button
          variant="transparent"
          size="small"
          tone="neutral"
          onClick={() => {
            reportPapEvent(
              PAP_Click_DashProfilePage({
                dashProfileAction: showAll ? "show_less" : "show_more",
                featureLine: "people",
                dashProfileSection: peopleType,
              }),
            );
            setShowAll(!showAll);
          }}
          style={{ marginLeft: "auto", marginRight: "4px" }}
        >
          {showAll
            ? i18n.t("user_profile_list_show_less")
            : i18n.t("user_profile_list_show_more")}
        </Button>
      ) : null}
    </Stack>
  );
};
